import { trackClickEvent } from '@frontend/tracking';
import { Translate } from '@frontend/translation';
import { Typography, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';
import { RadioGroup } from '@frontend/ui-elements';
import {
  FakeSeparator,
  ImageWrapper,
  BackgroundWrapper,
  HeadingWrapper,
  LayoutWrapper,
  LeftWrapper,
  RightWrapper,
  CTAWrapper,
} from '../GuestPageLayout/GuestPageLayout.css';
import PlatformHeader from '@customer-platform-shared/components/PlatformHeader/PlatformHeader';
import DashboardImage from '@customer-platform-assets/images/dashboard.png';
import { HEADER_VARIANTS } from '@customer-platform-shared/components/PlatformHeader/PlatformHeader.types';
import { Button, InnerTypography } from '@frontend/ui-elements';
import { PLATFORM_ROUTES } from '@frontend/routing';
import {
  IBuyerSignUpData,
  IBuyerSignUpErrors,
  ISellerSignUpData,
  ISellerSignUpErrors,
  useSignUpBuyer,
  useSignUpSeller,
} from '@frontend/api';
import { SubmitHandler } from 'react-hook-form';
import FormType from '@customer-platform-shared/components/Forms/utils/FormType.types';
import SellerSignUpForm from './SellerSignUpForm/SellerSignUpForm';
import { SignUpFormsWrapper } from './SignUp.css';
import { useEffect } from 'react';
import { useAuth } from '@frontend/auth';
import useModal from '@customer-platform-shared/components/Modals/useModal/useModal';
import InfoModal from '@customer-platform-shared/components/Modals/InfoModal/InfoModal';
import SuccessModal from '@customer-platform-shared/components/Modals/SuccessModal/SuccessModal';
import { useSearchParams } from 'react-router-dom';
import BuyerSignUpForm from './BuyerSignUpForm/BuyerSignUpForm';
import { useState } from 'react';
import { CaptionBox } from './SignUpForms/SignUpForms.css';
import { assign } from 'lodash';

export function SignUp() {
  const intl = useIntl();
  const theme = useTheme();
  const { checkSession, setIsFirstTimeLogin } = useAuth();
  const [selectedOperation, setSelectedOperation] = useState('buy');
  const [formErrors, setFormErrors] = useState({});
  const [successNotified, setSuccessNotified] = useState(false);
  const [searchParams, _] = useSearchParams();

  const handleOperationChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSelectedOperation(event.target.value);
  };

  const {
    openSuccessModal,
    openInfoModal,
    closeModal,
    isSuccessModalOpen,
    isInfoModalOpen,
  } = useModal();

  const {
    mutate: sellerMutate,
    isSuccess: isSellerSuccess,
    isPending: isSellerPending,
    error: sellerError,
  } = useSignUpSeller();
  const {
    mutate: buyerMutate,
    isSuccess: isBuyerSuccess,
    isPending: isBuyerPending,
    error: buyerError,
  } = useSignUpBuyer();

  const handleSellerFormSubmit: SubmitHandler<
    FormType<ISellerSignUpData>
  > = signUpSubmissionData => {
    sellerMutate(signUpSubmissionData as ISellerSignUpData);
  };

  const handleBuyerFormSubmit: SubmitHandler<
    FormType<IBuyerSignUpData>
  > = signUpSubmissionData => {
    buyerMutate(signUpSubmissionData as IBuyerSignUpData);
  };

  const OnboardingImage = () => {
    const imageId = 'signup.visual-onboarding.image';
    const imageAltText = intl.formatMessage({
      id: 'customer-platform.signup.visual-onboarding.image-alt',
    });

    return (
      <ImageWrapper
        id={imageId}
        onClick={() =>
          trackClickEvent({
            elementId: imageId,
            elementText: imageAltText,
          })
        }
      >
        <img src={DashboardImage} alt={imageAltText} loading="lazy" />
      </ImageWrapper>
    );
  };

  const openNotifications = () => {
    setIsFirstTimeLogin(true);
    if (checkSession) checkSession();
  };

  useEffect(() => {
    switch (searchParams.get('user')) {
      case 'seller':
        setSelectedOperation('sell');
        break;
      case 'buyer':
      default:
        setSelectedOperation('buy');
        break;
    }
  }, [searchParams]);

  useEffect(() => {
    if ((isSellerSuccess || isBuyerSuccess) && !successNotified) {
      openSuccessModal();
      setSuccessNotified(true);
    }
  }, [openSuccessModal, isSellerSuccess, isBuyerSuccess, successNotified]);

  useEffect(() => {
    if (
      (buyerError || sellerError) &&
      !(buyerError?.response?.data || sellerError?.response?.data)
    ) {
      openInfoModal();
    }
  }, [openInfoModal, buyerError, sellerError]);

  useEffect(() => {
    if (buyerError?.response?.data) {
      setFormErrors(
        assign({}, ...Object.values(buyerError.response.data).filter(Boolean)),
      );
    }
  }, [buyerError]);

  useEffect(() => {
    if (sellerError?.response?.data) {
      setFormErrors(
        assign({}, ...Object.values(sellerError.response.data).filter(Boolean)),
      );
    }
  }, [sellerError]);

  return (
    <BackgroundWrapper>
      <PlatformHeader variant={HEADER_VARIANTS.GUEST} />
      <LayoutWrapper>
        <LeftWrapper>
          <HeadingWrapper>
            <Typography component="h2" variant="h2">
              <InnerTypography
                message={intl.formatMessage({
                  id: 'customer-platform.signup.heading',
                })}
                variant="h2"
                color={theme.palette.primary[500]}
              />
            </Typography>
            <CTAWrapper>
              <Typography component="p" variant="p3">
                <InnerTypography
                  message={intl.formatMessage({
                    id: 'customer-platform.signup.subheading',
                  })}
                  variant="p3"
                  color={theme.palette.primary[500]}
                />
              </Typography>
              <Button
                buttonType="linked"
                size="large"
                id="headerCTA"
                data-testid="headerCTA"
                href={PLATFORM_ROUTES.login}
              >
                <Translate id="customer-platform.shared.login.login-form.button.login" />
              </Button>
            </CTAWrapper>
            {isInfoModalOpen && (
              <InfoModal
                title={intl.formatMessage({
                  id: 'customer-platform.shared.generic-error',
                })}
                primaryCta={{
                  text: intl.formatMessage({
                    id: 'customer-platform.shared.close',
                  }),
                  onClick: () => {
                    closeModal();
                  },
                }}
                severity="warning"
              />
            )}
            {isSuccessModalOpen && (
              <SuccessModal
                title={intl.formatMessage({
                  id: isBuyerSuccess
                    ? 'customer-platform.signup.forms.buyer-signup.success-modal.title'
                    : 'customer-platform.signup.forms.seller-signup.success-modal.title',
                })}
                text={intl.formatMessage({
                  id: isBuyerSuccess
                    ? 'customer-platform.signup.forms.buyer-signup.success-modal.text'
                    : 'customer-platform.signup.forms.seller-signup.success-modal.text',
                })}
                primaryCta={{
                  text: intl.formatMessage({
                    id: isBuyerSuccess
                      ? 'customer-platform.signup.forms.buyer-signup.success-modal.cta.primary'
                      : 'customer-platform.signup.forms.seller-signup.success-modal.cta.primary',
                  }),
                  onClick: () => {
                    openNotifications();
                  },
                }}
                secondaryCta={{
                  text: intl.formatMessage({
                    id: isBuyerSuccess
                      ? 'customer-platform.signup.forms.buyer-signup.success-modal.cta.secondary'
                      : 'customer-platform.signup.forms.seller-signup.success-modal.cta.secondary',
                  }),
                  onClick: () => {
                    window.location.href = import.meta.env.VITE_STATIC_APP_URL;
                  },
                }}
              />
            )}
          </HeadingWrapper>
          <SignUpFormsWrapper>
            <CaptionBox>
              <Typography variant="p1">
                <Translate id="customer-platform.signup.forms.heading" />
              </Typography>
              <Typography component="p" variant="caption2">
                <Translate id="customer-platform.signup.forms.subheading" />
              </Typography>
            </CaptionBox>
            <RadioGroup
              label={intl.formatMessage({
                id: 'customer-platform.signup.options',
              })}
              options={[
                {
                  value: 'buy',
                  label: intl.formatMessage({
                    id: 'customer-platform.signup.option.buy',
                  }),
                },
                {
                  value: 'sell',
                  label: intl.formatMessage({
                    id: 'customer-platform.signup.option.sell',
                  }),
                },
              ]}
              value={selectedOperation}
              onChange={handleOperationChange}
            />
            {selectedOperation === 'buy' && (
              <BuyerSignUpForm
                errors={formErrors as IBuyerSignUpErrors}
                isSuccess={isBuyerSuccess}
                isPending={isBuyerPending}
                onSubmit={handleBuyerFormSubmit}
              />
            )}
            {selectedOperation === 'sell' && (
              <SellerSignUpForm
                errors={formErrors as ISellerSignUpErrors}
                isSuccess={isSellerSuccess}
                isPending={isSellerPending}
                onSubmit={handleSellerFormSubmit}
              />
            )}
          </SignUpFormsWrapper>
        </LeftWrapper>
        <FakeSeparator />
        <RightWrapper>
          <OnboardingImage />
          <HeadingWrapper>
            <Typography
              component="h3"
              variant="h2"
              color={theme.palette.primary[500]}
            >
              <Translate id="customer-platform.signup.visual-onboarding.title" />
            </Typography>
            <Typography variant="p1" color={theme.palette.primary[500]}>
              <Translate id="customer-platform.signup.visual-onboarding.paragraph" />
            </Typography>
          </HeadingWrapper>
        </RightWrapper>
      </LayoutWrapper>
    </BackgroundWrapper>
  );
}

export default SignUp;
